// ============================================================================
// GST CLIENT TURNOVER (ADR-0021 §4)
// ============================================================================
// Previous-year aggregate turnover, per CLIENT per financial year — and what it
// implies.
//
// IT PROPOSES; IT NEVER ENFORCES. Nothing on this screen blocks a QRMP election
// or a filing. The figure may be stale or provisional, and overruling a
// professional on a number we did not audit would be worse than the problem.
//
// Selecting a row shows the assessment for the FOLLOWING year, because the
// statutory tests are on previous-year turnover: a 2025-26 figure is what
// decides 2026-27.
//
// Follows the mandatory screen blueprint in prima-web/CLAUDE.md.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader, ConfirmDialog } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  ActionMenu,
  type ActionMenuItem,
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
} from 'components/ui';
import notificationService from 'services/notificationService';
import { gstTurnoverService } from 'api/services/gstTurnoverService';
import GstTurnoverModal from 'components/modals/modules/gst/GstTurnoverModal';
import type { GstTurnover, GstApplicability } from 'api/types/gstTurnover.types';

interface Props {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  allowedActions?: string[];
}

const SOURCE_FILTER_OPTIONS = [
  { value: '', label: 'All sources' },
  { value: 'DECLARED', label: 'Declared' },
  { value: 'AUDITED', label: 'Audited' },
  { value: 'COMPUTED', label: 'Computed' },
];

const fyLabel = (year: number) => `FY ${year}-${String((year + 1) % 100).padStart(2, '0')}`;

const currentFinancialYear = (): number => {
  const now = new Date();
  return now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
};

/**
 * Crore and lakh — how the figure is quoted and how every threshold is written.
 * A raw 50000000 makes the reader count digits against a limit they know as
 * "5 crore".
 */
const money = (rupees?: number | null): string => {
  if (rupees === null || rupees === undefined) return '—';
  if (rupees >= 1e7) return `₹${(rupees / 1e7).toFixed(2).replace(/\.00$/, '')} cr`;
  if (rupees >= 1e5) return `₹${(rupees / 1e5).toFixed(2).replace(/\.00$/, '')} lakh`;
  return `₹${rupees.toLocaleString('en-IN')}`;
};

/** Unknown reads differently from both yes and no, because it is the one worth chasing. */
const flagTone = (applicable?: boolean | null) =>
  applicable === null || applicable === undefined
    ? { label: 'Unknown', cls: 'badge badge-inactive', colour: '#d97706' }
    : applicable
      ? { label: 'Yes', cls: 'badge badge-active', colour: '#16a34a' }
      : { label: 'No', cls: 'badge badge-inactive', colour: 'var(--text-secondary)' };

const GstTurnoverScreen: React.FC<Props> = ({ moduleIcon, tabId, moduleName, allowedActions }) => {
  void tabId; void moduleName;

  const canAdd = !allowedActions || allowedActions.includes('add');
  const canEdit = !allowedActions || allowedActions.includes('edit');

  const [rows, setRows] = useState<GstTurnover[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  const [yearFilter, setYearFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [draftYear, setDraftYear] = useState('');
  const [draftSource, setDraftSource] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState<'create' | 'edit'>('create');
  const [selected, setSelected] = useState<GstTurnover | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<GstTurnover | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // The assessment for the row in focus. Held separately because it is derived
  // server-side from the client's WHOLE history, not from the row.
  const [assessed, setAssessed] = useState<GstTurnover | null>(null);
  const [applicability, setApplicability] = useState<GstApplicability | null>(null);
  const [assessing, setAssessing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await gstTurnoverService.getAll({
        page,
        pageSize,
        searchTerm: searchQuery || undefined,
        financialYear: yearFilter ? Number(yearFilter) : undefined,
        source: sourceFilter || undefined,
      });
      setRows(result.data);
      setTotalItems(result.pagination.totalItems);
    } catch (err: any) {
      notificationService.toast.error(
        err?.response?.data?.message ?? 'Could not load turnover figures.',
      );
      setRows([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchQuery, yearFilter, sourceFilter]);

  useEffect(() => { void load(); }, [load]);

  // Debounced search, resetting to the first page — a search that leaves you on
  // page 4 of a 2-page result shows nothing and looks broken.
  useEffect(() => {
    const timer = setTimeout(() => setPage(1), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const assess = useCallback(async (row: GstTurnover) => {
    setAssessed(row);
    setApplicability(null);
    setAssessing(true);
    try {
      // The year AFTER the one recorded: the statutory tests read previous-year
      // turnover, so a 2025-26 figure is what decides 2026-27.
      setApplicability(
        await gstTurnoverService.getApplicability(row.clientId, row.financialYear + 1));
    } catch (err: any) {
      notificationService.toast.error(
        err?.response?.data?.message ?? 'Could not assess this client.',
      );
      setAssessed(null);
    } finally {
      setAssessing(false);
    }
  }, []);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await gstTurnoverService.remove(deleteTarget.id);
      notificationService.toast.success('Turnover figure removed.');
      setDeleteTarget(null);
      if (assessed?.id === deleteTarget.id) { setAssessed(null); setApplicability(null); }
      await load();
    } catch (err: any) {
      notificationService.toast.error(
        err?.response?.data?.message ?? 'Could not remove the turnover figure.',
      );
    } finally {
      setIsDeleting(false);
    }
  }, [deleteTarget, assessed, load]);

  const getRowActions = useCallback((row: GstTurnover): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [
      {
        id: 'assess',
        label: `What this implies for ${fyLabel(row.financialYear + 1)}`,
        icon: <IconSystem.eye size={16} />,
        onClick: () => { void assess(row); },
      },
    ];
    if (canEdit) {
      items.push({
        id: 'edit',
        label: 'Correct figure',
        icon: <IconSystem.edit size={16} />,
        onClick: () => { setSelected(row); setMode('edit'); setModalOpen(true); },
      });
      items.push({
        id: 'delete',
        label: 'Remove',
        icon: <IconSystem.trash size={16} />,
        onClick: () => setDeleteTarget(row),
        variant: 'danger',
      });
    }
    return items;
  }, [canEdit, assess]);

  const columns: SyutGridColumn<GstTurnover>[] = useMemo(() => [
    {
      id: 'client',
      header: 'Client',
      accessorKey: 'clientLegalName',
      size: 260,
      enableSorting: true,
      cell: ({ row }) => row.original.clientLegalName || '—',
    },
    {
      id: 'financialYear',
      header: 'Financial year',
      accessorKey: 'financialYear',
      size: 130,
      enableSorting: true,
      cell: ({ row }) => fyLabel(row.original.financialYear),
    },
    {
      id: 'aggregateTurnover',
      header: 'Aggregate turnover',
      accessorKey: 'aggregateTurnover',
      size: 160,
      enableSorting: true,
      cell: ({ row }) => <strong>{money(row.original.aggregateTurnover)}</strong>,
    },
    {
      id: 'source',
      header: 'Source',
      accessorKey: 'source',
      size: 120,
      enableSorting: true,
      // Coloured, because an AUDITED figure and a DECLARED one should not carry
      // the same weight when a warning cites them.
      cell: ({ row }) => (
        <span className={row.original.source === 'AUDITED' ? 'badge badge-active' : 'badge badge-inactive'}>
          {row.original.source}
        </span>
      ),
    },
    {
      id: 'notes',
      header: 'Notes',
      accessorKey: 'notes',
      size: 220,
      enableSorting: false,
      cell: ({ row }) => row.original.notes || '—',
    },
    {
      id: 'updatedByName',
      header: 'Last changed by',
      accessorKey: 'updatedByName',
      size: 150,
      enableSorting: true,
      cell: ({ row }) => row.original.updatedByName || row.original.createdByName || '—',
    },
  ], []);

  const actionButtons: ActionButton[] = useMemo(() => {
    const buttons: ActionButton[] = [];
    if (canAdd) {
      buttons.push({
        id: 'add',
        icon: <IconSystem.add size={16} />,
        label: 'Record',
        tooltip: 'Record a turnover figure',
        variant: 'primary' as const,
        onClick: () => { setSelected(null); setMode('create'); setModalOpen(true); },
      });
    }
    buttons.push({
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      tooltip: 'Refresh',
      variant: 'icon' as const,
      onClick: () => { void load(); },
    });
    return buttons;
  }, [canAdd, load]);

  const yearOptions = useMemo(() => {
    const latest = currentFinancialYear();
    const years = [{ value: '', label: 'All years' }];
    for (let y = latest; y >= 2017; y -= 1) years.push({ value: String(y), label: fyLabel(y) });
    return years;
  }, []);

  const activeFilterCount = (yearFilter ? 1 : 0) + (sourceFilter ? 1 : 0);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="GST Turnover"
        description="Previous-year aggregate turnover per client — across all GSTINs of the PAN. Pre-fills HSN digits and flags QRMP, e-invoicing and GSTR-9C, without blocking anything."
        icon={
          moduleIcon && (IconSystem as any)[moduleIcon]
            ? React.createElement((IconSystem as any)[moduleIcon], { size: 18 })
            : <IconSystem.dollarSign size={18} />
        }
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search client or notes…',
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: filterOpen || activeFilterCount > 0,
          activeFilterCount,
        }}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>

            {/* The assessment for the row in focus. Shown inline rather than in
                a modal because it is the REASON the figure is recorded, and
                burying it a click away makes the screen look like data entry. */}
            {assessed && (
              <div
                style={{
                  border: '1px solid var(--border-color)', background: 'var(--bg-secondary)',
                  borderRadius: 6, padding: '0.75rem', marginBottom: 8, flexShrink: 0,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--text-primary)' }}>
                    <strong>{assessed.clientLegalName}</strong> — what {fyLabel(assessed.financialYear)}{' '}
                    turnover of {money(assessed.aggregateTurnover)} implies for{' '}
                    <strong>{fyLabel(assessed.financialYear + 1)}</strong>
                    {applicability?.previousYearSource && (
                      <span style={{ color: 'var(--text-secondary)' }}>
                        {' '}(figure is {applicability.previousYearSource.toLowerCase()})
                      </span>
                    )}
                  </div>
                  <button
                    className="btn btn-secondary"
                    style={{ fontSize: '0.7rem', padding: '2px 8px' }}
                    onClick={() => { setAssessed(null); setApplicability(null); }}
                  >
                    Close
                  </button>
                </div>

                {assessing && (
                  <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginTop: 8 }}>
                    Assessing…
                  </div>
                )}

                {!assessing && applicability && (
                  <div
                    style={{
                      display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                      gap: 8, marginTop: 8,
                    }}
                  >
                    {applicability.flags.map((f) => {
                      const tone = flagTone(f.applicable);
                      return (
                        <div
                          key={f.code}
                          style={{
                            border: '1px solid var(--border-color)', borderRadius: 6,
                            padding: '0.5rem 0.625rem', background: 'var(--bg-primary)',
                            borderLeft: `3px solid ${tone.colour}`,
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                            <strong style={{ fontSize: '0.8125rem', color: 'var(--text-primary)' }}>
                              {f.label}
                            </strong>
                            <span className={tone.cls}>{tone.label}</span>
                          </div>
                          <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 4 }}>
                            {f.explanation}
                          </div>
                          {f.notificationRef && (
                            <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: 2 }}>
                              {f.notificationRef}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', marginTop: 8 }}>
                  Advisory only — none of this prevents an election or a filing.
                </div>
              </div>
            )}

            <SyutGrid
              data={rows as any}
              columns={columns as any}
              enableSorting
              enablePagination
              manualPagination
              totalCount={totalItems}
              currentPage={page - 1}
              pageSize={pageSize}
              onPageChange={(idx: number) => setPage(idx + 1)}
              onPageSizeChange={(size: number) => { setPageSize(size); setPage(1); }}
              rowActions={getRowActions as any}
              emptyMessage={loading ? 'Loading…' : 'No turnover recorded yet.'}
            />
          </div>
        </div>

        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <h3 className="search-panel-title">Filters</h3>
              <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
                <IconSystem.x size={14} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <SyutSelect
                  label="Financial year"
                  options={yearOptions}
                  value={draftYear}
                  isMulti={false}
                  isClearable={false}
                  size="sm"
                  onChange={(v) => setDraftYear((v as string) || '')}
                />
              </div>
              <div className="filter-section">
                <SyutSelect
                  label="Source"
                  options={SOURCE_FILTER_OPTIONS}
                  value={draftSource}
                  isMulti={false}
                  isClearable={false}
                  size="sm"
                  onChange={(v) => setDraftSource((v as string) || '')}
                />
              </div>
            </div>
            <div className="filter-actions">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setDraftYear(''); setDraftSource('');
                  setYearFilter(''); setSourceFilter(''); setPage(1);
                }}
              >
                Clear
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setYearFilter(draftYear); setSourceFilter(draftSource);
                  setPage(1); setFilterOpen(false);
                }}
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>

      <GstTurnoverModal
        isOpen={modalOpen}
        mode={mode}
        record={selected}
        onClose={() => setModalOpen(false)}
        onSaved={() => { void load(); }}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Remove this turnover figure?"
        message={
          `${money(deleteTarget?.aggregateTurnover)} for ${deleteTarget ? fyLabel(deleteTarget.financialYear) : ''}. `
          + 'Removing it makes QRMP, e-invoicing, GSTR-9C and HSN digits read as unknown for the '
          + 'year it governed — which is honest, but they will stop being suggested.'
        }
        confirmLabel="Remove"
        cancelLabel="Cancel"
        variant="danger"
        loading={isDeleting}
        loadingMessage="Removing…"
        onConfirm={() => { void handleDelete(); }}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};

export default GstTurnoverScreen;
